/**
 * Connector Fresh Start / Remove All / wipe paths return structured JSON for
 * boot-queue and reset contention (`library_still_starting`, `library_busy_reset`)
 * plus plain I/O failures. UI must never fall back to "Unknown error".
 */

import { errorText } from './structuredErrors';

export type LibraryMaintenanceErrorCode =
  | 'library_still_starting'
  | 'library_busy_reset'
  | 'library_busy_scanning'
  | 'library_rebuilding'
  | (string & {});

export type LibraryMaintenanceError = {
  code: LibraryMaintenanceErrorCode;
  message?: string;
  holder?: string;
};

const STILL_STARTING_CODE = 'library_still_starting';
const BUSY_SCANNING_CODE = 'library_busy_scanning';

export const LIBRARY_STILL_STARTING_USER_MESSAGE =
  'Library is still starting — your request is queued and will run automatically.';

export const LIBRARY_BUSY_SCANNING_USER_MESSAGE =
  'Library is busy while a scan is running — try again in a moment.';

export const LIBRARY_ALBUM_NOT_FOUND_USER_MESSAGE =
  'That album is no longer in your library. Refresh the page and try again.';

export const LIBRARY_REBUILDING_USER_MESSAGE = 'Rebuilding library…';

const STRUCTURED_CODES = new Set([
  'library_still_starting',
  'library_busy_reset',
  'library_busy_scanning',
  'library_rebuilding',
]);

const ALBUM_NOT_FOUND_RE = /^album not found for id '/i;

/** Connector local_library_get_album miss — stale wall tile or removed album. */
export function isAlbumNotFoundError(error: unknown): boolean {
  const text = errorText(error).trim();
  return ALBUM_NOT_FOUND_RE.test(text);
}

function parseStructuredPayload(text: string): LibraryMaintenanceError | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    const code = String(parsed.code ?? '').trim();
    if (!code) return null;

    return {
      code,
      message: typeof parsed.message === 'string' ? parsed.message : undefined,
      holder: typeof parsed.holder === 'string' ? parsed.holder : undefined,
    };
  } catch {
    return null;
  }
}

/** Parse structured reset/maintenance payload from IPC/HTTP error text. */
export function parseLibraryMaintenanceError(error: unknown): LibraryMaintenanceError | null {
  const text = errorText(error).trim();
  if (!text) return null;

  const mentionsStructured = [...STRUCTURED_CODES].some((code) => text.includes(code));
  const jsonStart = text.indexOf('{');
  const looksStructured = jsonStart >= 0 && text.includes('"code"');

  if (!mentionsStructured && !looksStructured) return null;

  if (jsonStart >= 0) {
    const structured = parseStructuredPayload(text.slice(jsonStart));
    if (structured) return structured;
  }

  for (const code of STRUCTURED_CODES) {
    if (text.includes(code)) {
      return { code };
    }
  }

  return null;
}

export { formatLibraryMaintenanceErrorMessage } from './libraryMaintenanceErrorFormat';
